import { createElement, type HTMLAttributes, type ReactNode } from 'react';
import DOMPurify from 'dompurify';

import { MathText } from './MathText';

// The one pipeline that turns authored chapter HTML into React.
//
// It was duplicated between the on-screen renderer (TextUnit) and the PDF export
// (printContentUnits), and the copies drifted: the print one passed every class
// through unfiltered, dropped inline styles entirely, and had no blank-line
// handling, so alignment and blank lines silently vanished from PDFs. Worse, the
// target="_blank" fix had to be applied twice, and the dead tabnabbing guard it
// replaced existed in both.
//
// A PDF export exists to reproduce what a student sees, so the shared behaviour
// is the on-screen behaviour.

export type HeadingLevel = 2 | 3 | 4;

const VOID_ELEMENT_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

// Block elements that should occupy a full line even when empty. TipTap
// serializes a blank line as an empty `<p></p>`, which has no line box and
// collapses on render. We give such elements a `<br>` so the vertical
// whitespace matches what the editor shows (and the legacy `<p><br></p>` markup).
const LINE_BLOCK_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'li',
  'blockquote',
]);

// Table-structural elements may only contain other table elements. The HTML
// parser keeps whitespace-only text between rows and cells (indentation in
// authored markup), and React warns and misplaces it if we render it, so it is
// dropped inside these.
const TABLE_STRUCTURE_TAGS = new Set([
  'colgroup',
  'table',
  'tbody',
  'tfoot',
  'thead',
  'tr',
]);

// HTML attributes whose React prop name differs. Passing the HTML spelling makes
// React drop the attribute with a console warning, so a colspan authored in a
// table would silently stop spanning.
const REACT_ATTRIBUTE_NAMES: Record<string, string> = {
  colspan: 'colSpan',
  rowspan: 'rowSpan',
  srcset: 'srcSet',
};

// Authored content may only keep the Quill classes the app actually styles.
// These are defined globally in styles/quill-compat.css, so they work in the PDF
// too — the print container already carries `lesson-content`.
const ALLOWED_QUILL_CLASS_NAMES = new Set([
  'ql-align-center',
  'ql-align-right',
  'ql-align-justify',
  'ql-indent-1',
  'ql-indent-2',
  'ql-indent-3',
  'ql-indent-4',
  'ql-indent-5',
  'ql-indent-6',
  'ql-indent-7',
  'ql-indent-8',
  'ql-size-small',
  'ql-size-large',
  'ql-size-huge',
  'ql-font-serif',
  'ql-font-monospace',
  'ql-direction-rtl',
  'ql-video',
]);

export function clampHeadingLevel(level: HeadingLevel | undefined): HeadingLevel {
  if (!level) {
    return 2;
  }

  return Math.min(4, Math.max(2, level)) as HeadingLevel;
}

// The TipTap editor serializes text alignment as an inline
// `style="text-align: …"` rather than the legacy Quill `ql-align-*` class.
// We strip inline styles for safety, so map the one supported style — text
// alignment — onto the equivalent Quill class the renderer already styles.
function alignmentClassFromStyle(style: string): string | undefined {
  const match = /text-align\s*:\s*(center|right|justify)/i.exec(style);

  if (!match) {
    return undefined;
  }

  return `ql-align-${match[1].toLowerCase()}`;
}

// mergeNoopenerRel keeps whatever rel the author wrote and guarantees the two
// tokens that make target="_blank" safe.
function mergeNoopenerRel(existingRel: string | null) {
  const tokens = new Set((existingRel ?? '').split(/\s+/).filter(Boolean));
  tokens.add('noopener');
  tokens.add('noreferrer');
  return Array.from(tokens).join(' ');
}

function getReactProps(
  tagName: string,
  element: Element,
  key: string,
): HTMLAttributes<HTMLElement> & { key: string } {
  const props: Record<string, unknown> = { key };
  const classNames: string[] = [];

  for (const attribute of Array.from(element.attributes)) {
    if (attribute.name === 'class') {
      attribute.value
        .split(/\s+/)
        .filter(Boolean)
        .filter((className) => ALLOWED_QUILL_CLASS_NAMES.has(className))
        .forEach((className) => classNames.push(className));
      continue;
    }

    if (attribute.name === 'for') {
      props.htmlFor = attribute.value;
      continue;
    }

    if (attribute.name === 'style') {
      const alignmentClass = alignmentClassFromStyle(attribute.value);

      if (alignmentClass) {
        classNames.push(alignmentClass);
      }
      continue;
    }

    const propName = REACT_ATTRIBUTE_NAMES[attribute.name] ?? attribute.name;

    if (propName === 'colSpan' || propName === 'rowSpan') {
      const span = Number(attribute.value);
      props[propName] = Number.isFinite(span) && span > 0 ? span : undefined;
      continue;
    }

    props[propName] = attribute.value;
  }

  if (classNames.length > 0) {
    props.className = Array.from(new Set(classNames)).join(' ');
  }

  // A link opened in a new tab can reach back through window.opener unless rel
  // carries noopener, so it is FORCED rather than only supplied when rel is
  // absent — an author writing rel="author" alongside target="_blank" would
  // otherwise be left unprotected.
  if (tagName === 'a' && element.getAttribute('target') === '_blank') {
    props.rel = mergeNoopenerRel(element.getAttribute('rel'));
  }

  return props as unknown as HTMLAttributes<HTMLElement> & { key: string };
}

function renderDomNode(node: ChildNode, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return <MathText key={key} text={node.textContent ?? ''} />;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as Element;
  const tagName = element.tagName.toLowerCase();

  if (VOID_ELEMENT_TAGS.has(tagName)) {
    return createElement(tagName, getReactProps(tagName, element, key));
  }

  const childNodes = TABLE_STRUCTURE_TAGS.has(tagName)
    ? Array.from(element.childNodes).filter(
        (childNode) =>
          childNode.nodeType !== Node.TEXT_NODE ||
          (childNode.textContent ?? '').trim() !== '',
      )
    : Array.from(element.childNodes);

  const children = childNodes.map((childNode, index) =>
    renderDomNode(childNode, `${key}-${index}`),
  );

  // Preserve blank lines: an empty block element collapses with no line box, so
  // render a <br> to keep the editor's vertical spacing.
  if (
    LINE_BLOCK_TAGS.has(tagName) &&
    element.children.length === 0 &&
    (element.textContent ?? '').trim() === ''
  ) {
    return createElement(
      tagName,
      getReactProps(tagName, element, key),
      createElement('br', { key: `${key}-br` }),
    );
  }

  return createElement(tagName, getReactProps(tagName, element, key), children);
}

/**
 * Sanitizes authored chapter HTML and renders it as React elements.
 *
 * `target` is added to DOMPurify's allowlist because it is NOT in the defaults —
 * without it an authored target="_blank" is silently dropped and every external
 * link opens in the same tab. Nothing server-side touches this markup; it is
 * sanitized here, at render time.
 */
export function renderContentHtml(html: string): ReactNode {
  const sanitizedHtml = DOMPurify.sanitize(html, {
    ADD_ATTR: ['class', 'target'],
  });

  if (typeof DOMParser === 'undefined') {
    return <MathText text={sanitizedHtml} />;
  }

  const documentFragment = new DOMParser().parseFromString(
    sanitizedHtml,
    'text/html',
  );

  return Array.from(documentFragment.body.childNodes).map((node, index) =>
    renderDomNode(node, `content-${index}`),
  );
}
